import { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, RefreshCw, Layers, ShieldCheck, CheckCircle, AlertTriangle } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

export function CsrfTokenGenerator() {
  const [length, setLength] = useState(32);
  const [format, setFormat] = useState("hex");
  const [count, setCount] = useState(3);
  const [tokens, setTokens] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  // Interactive sandbox states
  const [sandboxToken, setSandboxToken] = useState("");
  const [submittedToken, setSubmittedToken] = useState("");
  const [validationResult, setValidationResult] = useState<"none" | "success" | "fail">("none");

  const generateTokens = () => {
    const chars = {
      hex: "0123456789abcdef",
      base64: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/",
      base64url: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_",
      alphanumeric: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789",
      base36: "0123456789abcdefghijklmnopqrstuvwxyz"
    }[format] || "0123456789abcdef";

    const newTokens = [];
    for (let c = 0; c < count; c++) {
      let t = "";
      for (let i = 0; i < length; i++) {
        t += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      newTokens.push(t);
    }
    setTokens(newTokens);
    if (newTokens.length > 0) {
      setSandboxToken(newTokens[0]);
      setSubmittedToken(newTokens[0]);
    }
  };

  useEffect(() => {
    generateTokens();
  }, [length, format, count]);

  const handleCopy = () => {
    if (!tokens.length) return;
    copyToClipboard(tokens.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleValidateSandbox = () => {
    if (!submittedToken || !sandboxToken) {
      setValidationResult("none");
      return;
    }
    if (submittedToken === sandboxToken) {
      setValidationResult("success");
    } else {
      setValidationResult("fail");
    }
  };

  return (
    <ToolPageLayout
      title="CSRF Token Generator Online - Secure Tokens"
      description="Generate cryptographically random CSRF (Cross-Site Request Forgery) protection tokens with custom lengths, encodings, and interactive validation sandbox models."
      h1="CSRF Token Generator"
      category="JWT & Security"
      categoryPath="/jwt-security"
      howToUse={[
        "Choose your preferred token output format: Hexadecimal, Base64, Alphanumeric, or Base36.",
        "Configure custom token length and desired batch count.",
        "Your tokens generate instantly! Copy them to secure your web headers.",
        "Test token mechanics inside the interactive CSRF Simulation Sandbox below."
      ]}
      faq={[
        { question: "What is an online CSRF token generator and how does it protect web apps?", answer: "An online CSRF token generator creates secure, random, high-entropy tokens used to validate client state in web apps. It protects apps by ensuring incoming state-changing requests carry a secret value that can only be known or accessed by an authenticated user, preventing external sites from forging unauthorized actions." },
        { question: "What is Cross-Site Request Forgery (CSRF) and how does it occur?", answer: "Cross-Site Request Forgery (CSRF) is a vulnerability where an attacker tricks an authenticated user's browser into executing an unwanted action on a trusted site. Since browsers automatically append active session cookies to cross-origin requests, the target site executes the malicious command believing it is legitimate." },
        { question: "How does the Double Submit Cookie pattern prevent CSRF?", answer: "In the Double Submit Cookie pattern, the server sets a secure, random CSRF token value in a cookie. When the client makes a state-altering request (such as POST/PUT), it must send that same token value inside an HTTP header (e.g., X-CSRF-Token) or form body. The server then validates that both values match." },
        { question: "What is the Synchronizer Token Pattern (STP) in CSRF defense?", answer: "The Synchronizer Token Pattern (STP) stores the generated CSRF token directly within the user's active server-side session. When a state-changing request is submitted, the server extracts the submitted token and verifies it matches the one kept in the server's session memory." },
        { question: "Do I need CSRF protection if my application uses JSON Web Tokens (JWT)?", answer: "It depends on where you store the JWT. If you store the JWT in standard local storage or session storage, the application is vulnerable to XSS but safe from CSRF. If you store the JWT in an HTTP-only Cookie, your application is safe from XSS but vulnerable to CSRF, requiring proper CSRF tokens." },
        { question: "Does the SameSite Cookie attribute fully eliminate the need for CSRF tokens?", answer: "Setting cookies to 'SameSite=Strict' or 'SameSite=Lax' provides strong protection by restricting cookie transmissions on cross-origin requests. However, CSRF tokens remain a critical defense-in-depth practice to protect older browsers that don't fully support the SameSite attribute." },
        { question: "What is the difference between CSRF and XSS (Cross-Site Scripting)?", answer: "XSS involves executing malicious JavaScript code inside a victim's browser to steal session tokens or log user keystrokes. CSRF, on the other hand, does not steal data directly, but rather tricks an authenticated browser into performing actions on the user's behalf without their consent." },
        { question: "How often should CSRF tokens be regenerated?", answer: "Ideally, CSRF tokens should be generated once per user session to maintain usability. However, for highly sensitive operations (like updating passwords, emails, or processing payments), regenerating a new token for that specific action is recommended to maximize security." },
        { question: "Where should the CSRF token be included in AJAX or Fetch requests?", answer: "In AJAX, Axios, or Fetch API calls, the CSRF token is commonly included inside custom HTTP request headers, such as 'X-CSRF-Token', 'X-XSRF-TOKEN', or 'Authorization-CSRF'. This approach is cleaner than embedding token fields in every individual JSON request body." },
        { question: "What length and format are recommended for secure CSRF tokens?", answer: "Secure CSRF tokens should be at least 32 characters (128 bits of entropy) and formatted using URL-safe characters, such as hexadecimal or Base64URL. This ensures they are strong against brute-force guessing and are safe to pass through forms and HTTP headers." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Controls Panel */}
          <div className="space-y-4 bg-white border border-slate-200 p-5 rounded-2xl shadow-xs">
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100 text-rose-600">
              <Layers className="w-5 h-5" />
              <span className="font-bold text-sm uppercase tracking-wider">Token Options</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Token Format</Label>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-rose-500 outline-none"
                >
                  <option value="hex">Hex (0-9, a-f)</option>
                  <option value="base64">Base64</option>
                  <option value="base64url">Base64URL</option>
                  <option value="alphanumeric">Alphanumeric</option>
                  <option value="base36">Base36 (0-9, a-z)</option>
                </select>
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-700">Token Length</Label>
                <Input
                  type="number"
                  min="8"
                  max="256"
                  value={length}
                  onChange={(e) => setLength(Math.max(8, Math.min(256, Number(e.target.value))))}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700">Tokens to Generate</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(50, Number(e.target.value))))}
                className="mt-1"
              />
            </div>

            <Button
              onClick={generateTokens}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white gap-2 mt-2"
            >
              <RefreshCw className="w-4 h-4" />
              Generate CSRF Tokens
            </Button>
          </div>

          {/* Outputs Panel */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-semibold text-slate-700">Generated CSRF Tokens</Label>
              <span className="text-xs text-slate-500">{tokens.length} created</span>
            </div>

            <Textarea
              value={tokens.join("\n")}
              readOnly
              className="min-h-[160px] font-mono text-xs leading-loose bg-slate-50"
            />

            <Button
              onClick={handleCopy}
              variant="outline"
              className="w-full gap-2 text-xs font-semibold border-slate-300"
            >
              <Copy className="w-4 h-4" />
              {copied ? "Copied All!" : "Copy Generated Tokens"}
            </Button>
          </div>
        </div>

        {/* CSRF Sandbox Simulation */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-rose-600 pb-2 border-b border-slate-200">
            <ShieldCheck className="w-5 h-5" />
            <h4 className="font-bold text-sm uppercase tracking-wider">CSRF Validation Sandbox</h4>
          </div>

          <div className="text-xs text-slate-600 leading-relaxed">
            Test the token handshake! Below represents the CSRF token stored securely in the browser cookie session, and the candidate token sent inside the client form submission.
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-500 uppercase">1. Server Session Token (Secure Cookie)</Label>
              <Input
                type="text"
                value={sandboxToken}
                onChange={(e) => setSandboxToken(e.target.value)}
                className="font-mono text-xs bg-slate-100"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-500 uppercase">2. Client Form/Header Token Submitted</Label>
              <Input
                type="text"
                value={submittedToken}
                onChange={(e) => setSubmittedToken(e.target.value)}
                className="font-mono text-xs"
                placeholder="Paste token to test..."
              />
            </div>
          </div>

          <div className="flex justify-between items-center pt-2">
            <Button
              onClick={handleValidateSandbox}
              size="sm"
              className="bg-slate-900 hover:bg-slate-800 text-white text-xs px-4"
            >
              Test Request Signature Validation
            </Button>

            {validationResult === "success" && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg font-semibold">
                <CheckCircle className="w-4 h-4 shrink-0 text-emerald-600" /> Matches! Session Authorized
              </div>
            )}

            {validationResult === "fail" && (
              <div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg font-semibold">
                <AlertTriangle className="w-4 h-4 shrink-0 text-red-600" /> Verification Failed: 403 Forbidden
              </div>
            )}
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
